import {
    Box,
    Button,
    TextField,
    Typography,
    Chip,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    CircularProgress,
    Backdrop,
    IconButton,
    Tabs,
    Tab,
} from "@mui/material";
import axiosInstance from "../../../api/axiosInstance";
import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { DeleteOutline, Add, PersonAdd, Delete, GroupAdd } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import WhatsappBusinessGroupCreate from "./WhatsappBusinessGroupCreate";
import WhatsappBusinessGroupEdit from "./WhatsappBusinessGroupEdit";
import WhatsappBusinessGroupDelete from "./WhatsappBusinessGroupDelete";
import base_url from "../../../constants";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const WhatsappBusinessGroupAdd = () => {
    const [activeTab, setActiveTab] = useState(1);
    const [totalRows, setTotalRows] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [fetchedGData, setFetchedGData] = useState([]);
    const [options, setOptions] = useState([]);
    const [searchedGName, setSearchGName] = useState("");
    const [filteredData, setFiltereData] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [userId, setUserId] = useState(Cookies.get("userId"));
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteModalOpening, setDeleteModalOpening] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(null);
    const [backdropOpen, setBackdropOpen] = useState(false);

    const showNotification = useNotification();

    // Fetching Options for Groups
    const fetchingOptions = async () => {
        try {
            const res = await axiosInstance.get(`${base_url}/whatsapp-business/groups`);
            console.log(res.data.data);
            if (res.data.success && res.data.data) {
                const groupNames = res.data.data.map((group) => group.group_name);
                setOptions(groupNames);
            }
        } catch (e) {
            console.error("Error: ", e);
            showNotification("Something went wrong please refresh the page", "error");
        }
    };

    useEffect(() => {
        fetchingOptions();
    }, [open, editOpen, deleteModalOpening, refresh]);

    // Fetching group data
    const fetchGroupData = async (groupName, page) => {
        if (!groupName) {
            showNotification("Please select a group name!", "error");
            return;
        }
        try {
            setBackdropOpen(true);
            showNotification("Loading contacts...", "info");
            let tempGroup = encodeURIComponent(groupName);

            const res = await axiosInstance.get(
                `${base_url}/whatsapp-business/groups/${tempGroup}/contacts?page=${page}&per_page=50`
            );
            
            if (res.data.success) {
                setFiltereData(res.data.data || []);
                setTotalRows(res.data.pagination?.total || 0);
                setTotalPage(res.data.pagination?.last_page || 1);
                setCurrentPage(res.data.pagination?.current_page || 1);
            } else {
                setFiltereData([]);
                setTotalRows(0);
                setTotalPage(1);
            }
        } catch (e) {
            console.error("Error: ", e);
            showNotification("Something went wrong please try again!", "error");
            setFiltereData([]);
            setTotalRows(0);
            setTotalPage(1);
        } finally {
            setBackdropOpen(false);
        }
    };

    const handleSelectionChange = (e) => {
        setSearchGName(e.target.value);
        setFiltereData([]);
        setCurrentPage(1);
        if (e.target.value) {
            fetchGroupData(e.target.value, 1);
        }
    };

    const handleRefresh = () => {
        fetchingOptions();
        if (searchedGName) {
            fetchGroupData(searchedGName, currentPage);
        }
    };

    // Page change function
    const handlePageChange = (event, value) => {
        const page = value;
        setCurrentPage(value);
        fetchGroupData(searchedGName, page);
    };

    const resetData = () => {
        setFiltereData([]);
        setSearchGName("");
        setCurrentPage(1);
    };

    return (
        <Box sx={WB_SX.page}>
            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={backdropOpen}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Typography variant="h6" sx={WB_SX.pageTitle}>
                Manage WhatsApp Business Groups
            </Typography>

            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ ...WB_SX.tabs, borderBottom: 1, borderColor: "divider" }}
            >
                    <Tab label="View Groups" value={1} />
                    <Tab label="Create Group" value={2} />
                    <Tab label="Add Contacts" value={3} />
                    <Tab label="Delete Contacts" value={4} />
            </Tabs>

            {activeTab === 1 && (
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            mb: 3,
                            alignItems: "center",
                        }}
                    >
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel size="small">Select Group</InputLabel>
                            <Select
                                value={searchedGName}
                                label="Select Group"
                                onChange={handleSelectionChange}
                                size="small"
                            >
                                {options.map((option, i) => (
                                    <MenuItem key={i} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <IconButton onClick={handleRefresh} color="primary">
                            <AutorenewIcon />
                        </IconButton>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={resetData}
                            size="small"
                        >
                            Clear
                        </Button>
                    </Box>

                    {filteredData.length > 0 && (
                        <Paper elevation={0} sx={WB_SX.tableContainer}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                            <TableCell sx={WB_SX.tableHeadCell}>Phone Number</TableCell>
                                            <TableCell sx={WB_SX.tableHeadCell}>Header Parameters</TableCell>
                                            <TableCell sx={WB_SX.tableHeadCell}>Body Parameters</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredData.map((contact, index) => (
                                            <TableRow key={index} sx={WB_SX.tableRow}>
                                                <TableCell>{contact.phone_number}</TableCell>
                                                <TableCell>
                                                    {contact.header_param && Array.isArray(contact.header_param) && contact.header_param.length > 0 ? (
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            {contact.header_param.map((param, paramIndex) => (
                                                                <Chip
                                                                    key={paramIndex}
                                                                    label={String(param)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No header parameters
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {contact.body_param && Array.isArray(contact.body_param) && contact.body_param.length > 0 ? (
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            {contact.body_param.map((param, paramIndex) => (
                                                                <Chip
                                                                    key={paramIndex}
                                                                    label={String(param)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="secondary"
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No body parameters
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {totalPage !== null && totalPage > 0 && (
                                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                                    <Pagination
                                        count={totalPage}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                            {totalRows !== null && (
                                <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
                                    Total Contacts: {totalRows}
                                    {totalPage > 1 && (
                                        <span> (Page {currentPage} of {totalPage})</span>
                                    )}
                                </Typography>
                            )}
                        </Paper>
                    )}

                    {searchedGName && filteredData.length === 0 && !backdropOpen && (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                            No contacts found in this group.
                        </Typography>
                    )}
                </Box>
            )}

            {activeTab === 2 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "400px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ mb: 2, color: "text.primary", fontWeight: 500 }}
                    >
                        Create New Group
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            color: "text.secondary",
                            maxWidth: "600px",
                            lineHeight: 1.6,
                        }}
                    >
                        Initiate a new group setup.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<GroupAdd />}
                        onClick={() => setOpen(true)}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            ...WB_SX.primaryButton,
                        }}
                    >
                        Create New Group
                    </Button>
                    <WhatsappBusinessGroupCreate
                        opening={() => setOpen(false)}
                        closing={() => setOpen(false)}
                        open={open}
                        onSuccess={() => {
                            setOpen(false);
                            fetchingOptions();
                        }}
                    />
                </Box>
            )}

            {activeTab === 3 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "400px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ mb: 2, color: "text.primary", fontWeight: 500 }}
                    >
                        Add Contacts to Group
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            color: "text.secondary",
                            maxWidth: "600px",
                            lineHeight: 1.6,
                        }}
                    >
                        Extend an existing group with additional contacts.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PersonAdd />}
                        onClick={() => setEditOpen(true)}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            ...WB_SX.primaryButton,
                        }}
                    >
                        Add Contacts to Group
                    </Button>
                    <WhatsappBusinessGroupEdit
                        open={editOpen}
                        onClose={() => setEditOpen(false)}
                        options={options}
                        onSuccess={() => {
                            setEditOpen(false);
                            if (searchedGName) {
                                fetchGroupData(searchedGName, currentPage);
                            }
                        }}
                    />
                </Box>
            )}

            {activeTab === 4 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "400px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ mb: 2, color: "text.primary", fontWeight: 500 }}
                    >
                        Delete Contacts from Group
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            color: "text.secondary",
                            maxWidth: "600px",
                            lineHeight: 1.6,
                        }}
                    >
                        Remove contacts or delete a group.
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        size="large"
                        startIcon={<Delete />}
                        onClick={() => setDeleteModalOpening(true)}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                        }}
                    >
                        Delete Contacts from Group
                    </Button>
                    <WhatsappBusinessGroupDelete
                        opening={() => setDeleteModalOpening(false)}
                        closing={() => setDeleteModalOpening(false)}
                        open={deleteModalOpening}
                        options={options}
                        onSuccess={() => {
                            setDeleteModalOpening(false);
                            if (searchedGName) {
                                fetchGroupData(searchedGName, currentPage);
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default WhatsappBusinessGroupAdd;

